import { CloseOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useState } from 'react';
import styled from 'styled-components';

import { Flex, Space } from '@/components/base';
import Button from '@/components/Button/Button';
import IconButton from '@/components/Button/IconButton';
import Input from '@/components/Inputs/Input';
import { useModalContext } from '@/components/Modal/Modal';
import Heading from '@/components/Typography/Heading';
import Typo from '@/components/Typography/Typo';
import {
  createHelpscoutInfo,
  type HelpscoutInfo,
} from '@/entities/(integration)/helpscout';
import useColor from '@/hooks/domain/useColor';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { modalStyle } from '@/styles/style';

type Props = {
  onConnectComplete: (k: HelpscoutInfo | null) => void;
};

const HelpscoutConnectModal = (props: Props) => {
  const currWorkspace = useWorkspaceStore((state) => state.currWorkspace);
  const color = useColor();
  const { closeModal } = useModalContext();

  const [apiKey, setApiKey] = useState('');
  const [apiSecret, setApiSecret] = useState('');
  const [error, setError] = useState('');

  const onConnectClick = async () => {
    if (apiKey.trim() === '') return;
    if (!currWorkspace) return;
    try {
      const data = await createHelpscoutInfo({
        workspace_id: currWorkspace.alias,
        client_id: apiKey,
        client_secret: apiSecret,
      });
      props.onConnectComplete(data);
    } catch {
      setError('Your token is invalid. Please check again!');
    }
  };
  return (
    <ModalStyle d="column">
      <Flex j="space-between">
        <Heading type="h2">Helpscout Integration</Heading>
        <IconButton
          icon={<CloseOutlined />}
          outlined={false}
          sizeType="l"
          onClick={closeModal}
        />
      </Flex>
      <Space h="24px" />
      <Flex j="space-between" a="end">
        <Typo type="label1" weight="m">
          App ID
        </Typo>
        <div
          style={{ cursor: 'pointer' }}
          onClick={() =>
            window.open(
              'https://developer.helpscout.com/mailbox-api/overview/authentication/#oauth2-application',
            )
          }
        >
          <Typo type="label2" style={{ color: color.blue500 }}>
            How to get your App ID{' '}
          </Typo>
          <InfoCircleOutlined style={{ color: color.blue500 }} />
        </div>
      </Flex>
      <Space h="16px" />
      <Input
        placeholder="Enter your App ID"
        sizeType="m"
        value={apiKey}
        onChange={(e) => setApiKey(e.target.value)}
      />
      <Space h="24px" />
      <Typo type="label1" weight="m">
        App secret
      </Typo>
      <Space h="16px" />
      <Input
        placeholder="Enter your App secret"
        sizeType="m"
        value={apiSecret}
        onChange={(e) => setApiSecret(e.target.value)}
      />
      <Space h="32px" />
      {error ? (
        <Flex j="space-between">
          <Typo type="label2" style={{ color: color.red600 }}>
            {error}
          </Typo>
          <Button
            sizeType="m"
            onClick={onConnectClick}
            disabled={apiKey.trim() === ''}
          >
            Connect
          </Button>
        </Flex>
      ) : (
        <Flex j="flex-end">
          <Button
            sizeType="m"
            onClick={onConnectClick}
            disabled={apiKey.trim() === '' || apiSecret.trim() === ''}
          >
            Connect
          </Button>
        </Flex>
      )}
    </ModalStyle>
  );
};

const ModalStyle = styled(Flex)`
  width: 620px;
  padding: 32px;
  color: ${(props) => props.theme.colors.gray800};
  ${modalStyle};
`;

export default HelpscoutConnectModal;
